/*
 * MIDI functionality based off https://surikov.github.io/webaudiofont/
 * Permission granted by the Author to use any license for this modified version (not restricted to GPL)
 */

import { Injectable, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject } from "rxjs";
import { Hymn } from "../hymn/hymn";
import { SettingsService } from "./settings.service";
import { translation as appTranslation } from "../translation";

declare var WebAudioFontPlayer: any;
declare var MIDIFile: any;

// --- NEW UNIFIED EVENT TYPE ---
export type MidiEvent =
  | { type: 'note', track: number, index: number, state: 'on' | 'off' }
  | { type: 'status', status: string };

@Injectable({
  providedIn: 'root'
})
export class MidiPlayerService {
  private audioContext: any;
  private player: any;
  private reverberator: any;
  private equalizer: any;
  private input: any;
  private masterGain!: GainNode;
  private limiter!: DynamicsCompressorNode;

  // State for playback
  private scheduler: any;
  playing: boolean = false;
  currentVerse: number = 0;
  playingChorus: boolean = false;
  public introPlaying: boolean = false;
  private schedulerIntroPlaying: boolean = false;
  progressInterval: any = null;

  // Voice instrument envelope (gentle attack/release for natural phrasing)
  private readonly VOICE_ATTACK_SEC = 0.02; // ~20ms fade-in
  private readonly VOICE_RELEASE_SEC = 0.1; // ~100ms fade-out

  // Accompaniment (second instrument) attenuation multiplier
  private readonly ACCOMP_VOLUME_MULTIPLIER = 0.7;

  // Time-based grouping window for near-simultaneous notes (seconds)
  private readonly GROUP_WINDOW_SEC = 0.06; // 60 ms

  // Timing and duration
  private timeBetweenRepeats: number = 1000; // milliseconds; overridden by SettingsService
  private songTimeOffset: number = 0;
  private audioTimeAtSpeedChange: number = 0;
  private bufferedTime: number = 0;
  private duration: number = 0;
  private introDuration: number = 0;
  private mainSongDuration: number = 0;
  private mainSongAudioTimeStart: number = 0;

  // Notes and instruments
  private scheduledNotes: any[] = [];

  // --- NEW UNIFIED EVENT STREAM ---
  public eventSubject = new BehaviorSubject<MidiEvent | null>(null);
  public event$ = this.eventSubject.asObservable();

  private perTrackNoteIdx: number[] = [];
  private nextNoteIndex: number = 0;
  private trackToInstrument: string[] = [];
  private trackToInstrumentType: string[] = [];
  private chorusStartTime: number | null = null;

  // Hymn and settings data
  hymnReference: Hymn | null = null;
  totalRepeats: number = 1;
  playbackSpeed = 1.0;
  private transposeSemitones: number = 0;
  public instrumentTypes: string[] = this.settingsService.getInstruments();
  private startsWithChorusFlag: boolean = false;

  // Public Observables for UI
  public progressSubject = new BehaviorSubject<number>(0);
  public progress$ = this.progressSubject.asObservable();

  // Actions for media controls
  nextButtonAction: (() => void) | null = null;
  previousButtonAction: (() => void) | null = null;

  // Other properties
  isGameMode = false;
  private timeouts: any[] = [];
  private audioContextTimeout: any;
  private performanceModeEnabled: boolean = false;

  private karaokeSlices: Array<{
    start: number;
    end: number;
    idx: number;
    onTimeout?: any;
    offTimeout?: any;
  } | null> = [];

  private pendingStartAt?: { section: 'verse'|'chorus', verseNo?: number, index: number, markerTrack: number };
  private verseDurationSec: number = 0;

  public appleSilentBackgroundSound = new Audio("data:audio/mp3;base64,//MkxAAHiAICWABElBeKPL/RANb2w+yiT1g/gTok//lP/W/l3h8QO/OCdCqCW2Cw//MkxAQHkAIWUAhEmAQXWUOFW2dxPu//9mr60ElY5sseQ+xxesmHKtZr7bsqqX2L//MkxAgFwAYiQAhEAC2hq22d3///9FTV6tA36JdgBJoOGgc+7qvqej5Zu7/7uI9l//MkxBQHAAYi8AhEAO193vt9KGOq+6qcT7hhfN5FTInmwk8RkqKImTM55pRQHQSq//MkxBsGkgoIAABHhTACIJLf99nVI///yuW1uBqWfEu7CgNPWGpUadBmZ////4sL//MkxCMHMAH9iABEmAsKioqKigsLCwtVTEFNRTMuOTkuNVVVVVVVVVVVVVVVVVVV//MkxCkECAUYCAAAAFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV");

  startAppleBackgroundSilence() {
    this.appleSilentBackgroundSound.loop = true;
    return this.appleSilentBackgroundSound.play();
  }

  private speedChangeInterval: any;

  constructor(
    private http: HttpClient,
    private settingsService: SettingsService,
    private zone: NgZone
  ) {
    this.transposeSemitones = this.settingsService.getPlaybackTranspose();
    document.addEventListener("controlsNotification", (event: any) => {
      this.handleControlsEvent({ message: event.message, position: 0 });
    });
  }

  public async playMidi(hymn: Hymn, instrumentTypes: string[] = this.settingsService.getInstruments(), repeatTimes: number, playbackSpeed: number = this.playbackSpeed, playIntro: boolean = this.settingsService.getIntroEnabled()?? true) {
    this.isGameMode = false;

    await this.settingsService.waitForSettings();
    this.transposeSemitones = this.settingsService.getPlaybackTranspose();
    this.timeBetweenRepeats = this.settingsService.getTimeBetweenRepeatsMs();

    this.stop();

    const filePath = `/assets/midi/${hymn.reference}.mid`;
    this.hymnReference = hymn;
    this.instrumentTypes = this.replaceRandomInstruments(instrumentTypes);
    this.playbackSpeed = playbackSpeed;
    this.totalRepeats = repeatTimes;

    const useSeparateIntro = (appTranslation as any).useSeparateIntroMidi === true;

    if (playIntro && useSeparateIntro) {
      const introPath = `/assets/intro/${hymn.reference}.mid`;
      this.http.get(introPath, { responseType: 'arraybuffer' }).subscribe({
        next: (introBuf) => {
          console.debug('[MIDI] Using separate intro file:', introPath);
          this.loadAndBuildScheduleWithIntroBuffer(filePath, introBuf, hymn.startsWithChorus ?? false);
        },
        error: async (err) => {
          console.debug('[MIDI] Intro file not found or failed to load, falling back to tail intro:', introPath, err?.status || err);
          let introTimestamps: { start: number, end: number } | null = null;
          if (hymn.timestamps?.intro) {
            introTimestamps = { start: hymn.timestamps.intro, end: hymn.timestamps.introEnd ?? Infinity };
          } else {
            const fullDuration = await this.getSongDuration(filePath);
            introTimestamps = { start: Math.max(0, fullDuration - 12), end: fullDuration };
          }
          this.loadAndBuildSchedule(filePath, introTimestamps, hymn.startsWithChorus ?? false);
        }
      });
      return;
    }

    // Default behavior (Afrikaans or intros disabled): timestamps-based or tail intro
    let introTimestamps: { start: number, end: number } | null = null;
    if (playIntro) {
      if (hymn.timestamps?.intro) {
        introTimestamps = { start: hymn.timestamps.intro, end: hymn.timestamps.introEnd ?? Infinity };
      } else {
        const fullDuration = await this.getSongDuration(filePath);
        introTimestamps = { start: Math.max(0, fullDuration - 12), end: fullDuration };
      }
    }

    this.loadAndBuildSchedule(filePath, introTimestamps, hymn.startsWithChorus?? false);
  }

  public async playHymnPieceForGame(hymn: Hymn, playDuration: number = 10, randomPosition: boolean = false, startTime: number = 0) {
    this.isGameMode = true;
    this.stop();

    const filePath = `/assets/midi/${hymn.reference}.mid`;
    this.hymnReference = hymn;
    this.playbackSpeed = 1.0;
    this.instrumentTypes = this.replaceRandomInstruments(['random', 'harp']);
    this.totalRepeats = 0;

    if (randomPosition) {
      const duration = await this.getSongDuration(filePath);
      startTime = Math.floor(Math.random() * (duration - playDuration));
    }

    const gameClip = { start: startTime, end: startTime + playDuration };
    this.loadAndBuildSchedule(filePath, gameClip);
  }

  public stop() {
    if (this.playing) {
      this.eventSubject.next({ type: 'status', status: 'stopped' });
      this.playing = false;
      this.introPlaying = false;
      this.schedulerIntroPlaying = false;
      this.playingChorus = false;
      this.progressSubject.next(0);
    }
    this.resetSchedulers();
    this.releaseAudioContextIfNotUsedSoon();
  }

  public restart(playbackSpeed: number = this.playbackSpeed) {
    if (this.hymnReference) {
      this.playMidi(this.hymnReference, this.instrumentTypes, this.totalRepeats, playbackSpeed, this.introPlaying);
    }
  }

  public changeInstrument(instrumentTypes: string[]) {
    if (!this.player || !this.audioContext) return;
    this.instrumentTypes = this.replaceRandomInstruments(instrumentTypes);
    const instrumentIds = this.loadInstrument(instrumentTypes);
    const numberOfInstruments = instrumentIds.length;

    for (let i = 0; i < this.trackToInstrument.length; i++) {
      const idx = i % numberOfInstruments;
      const instrument = this.player.loader.instrumentInfo(instrumentIds[idx]);
      this.trackToInstrument[i] = instrument.variable;
      this.trackToInstrumentType[i] = this.instrumentTypes[idx];
      this.player.loader.startLoad(this.audioContext, instrument.url, instrument.variable);
    }
  }

  public async playFromMarker(hymn: Hymn, opts: { section: 'verse'|'chorus', verseNo?: number, index: number, markerTrack: number, skipIntro?: boolean }) {
    const skipIntro = opts.skipIntro !== false;
    await this.playMidi(hymn, this.instrumentTypes, this.totalRepeats || 1, this.playbackSpeed, !skipIntro && (this.settingsService.getIntroEnabled() ?? true));
    this.pendingStartAt = { section: opts.section, verseNo: opts.verseNo, index: opts.index, markerTrack: opts.markerTrack };
  }

  public async restartFromMarker(opts: { section: 'verse'|'chorus', verseNo?: number, index: number, markerTrack: number }) {
    const hymn = this.hymnReference;
    if (!hymn) return;
    this.stop();
    await this.playFromMarker(hymn, { ...opts, skipIntro: true });
  }

  private computeTargetStartTime(section: 'verse'|'chorus', verseNo: number|undefined, index: number, markerTrack: number): number | null {
    if (!this.scheduledNotes || this.scheduledNotes.length === 0) return null;
    const track0 = markerTrack - 1;
    let inMain = true;
    let currentVerse = 0;
    let inChorus = false;

    const lastSliceStart: Array<number | null> = new Array(this.trackToInstrument.length).fill(null);
    const counts: number[] = new Array(this.trackToInstrument.length).fill(0);

    for (let i = 0; i < this.scheduledNotes.length; i++) {
      const n = this.scheduledNotes[i];

      if (n.event) {
        if (n.event === 'intro_ended') { inMain = true; continue; }
        if (typeof n.event === 'string' && n.event.startsWith('verse_start:')) {
          currentVerse = parseInt(n.event.split(':')[1], 10) || 0;
          inChorus = false;
          for (let t = 0; t < counts.length; t++) { counts[t] = 0; lastSliceStart[t] = null; }
          continue;
        }
        if (n.event === 'chorus_start') {
          inChorus = true;
          for (let t = 0; t < counts.length; t++) { counts[t] = 0; lastSliceStart[t] = null; }
          continue;
        }
      }

      if (n.pitch === undefined || n.duration === undefined) continue;
      if (!inMain) continue;

      const t = n.trackIndex;
      const start = n.when;
      const end = n.when + n.duration;
      const last = lastSliceStart[t];
      if (last == null || (start - last) > this.GROUP_WINDOW_SEC) {
        lastSliceStart[t] = start;
        counts[t] = (counts[t] || 0) + 1;

        const targetVerseToFind = (this.totalRepeats === 1) ? 1 : (verseNo || 0);
        const matchesSection = (section === 'verse') ? (!inChorus && currentVerse === targetVerseToFind) : (inChorus);

        if (t === track0 && matchesSection && counts[t] === index) {
          return start;
        }
      }
    }
    return null;
  }

  public hasAudioContext(): boolean {
    return this.audioContext !== null;
  }

  private loadAndBuildSchedule(path: string, intro: { start: number, end: number } | null, startsWithChorus: boolean = false) {
    this.http.get(path, { responseType: 'arraybuffer' }).subscribe({
      next: (response) => {
        this.initAudioContext(false);
        this.player = new WebAudioFontPlayer();
        this.equalizer = this.player.createChannel(this.audioContext);
        this.reverberator = this.player.createReverberator(this.audioContext);

        this.masterGain = this.audioContext.createGain();
        this.masterGain.gain.value = 0.5; // -6 dB
        this.limiter = this.audioContext.createDynamicsCompressor();
        this.limiter.threshold.value = -6;
        this.limiter.knee.value = 4;
        this.limiter.ratio.value = 3;
        this.limiter.attack.value = 0.003;
        this.limiter.release.value = 0.25;

        this.input = this.equalizer.input;

        if (this.performanceModeEnabled) {
          this.equalizer.output.connect(this.limiter);
        } else {
          this.equalizer.output.connect(this.reverberator.input);
          this.reverberator.output.connect(this.limiter);
        }
        this.limiter.connect(this.masterGain);
        this.masterGain.connect(this.audioContext.destination);

        const midiFile = new MIDIFile(response);
        const songStructure = midiFile.parseSong();
        this.trackToInstrument = new Array(songStructure.tracks.length);

        const instrumentIds = this.loadInstrument(this.instrumentTypes);
        const numberOfInstruments = instrumentIds.length;
        for (let i = 0; i < songStructure.tracks.length; i++) {
          const idx = i % numberOfInstruments;
          const info = this.player.loader.instrumentInfo(instrumentIds[idx]);
          this.trackToInstrument[i] = info.variable;
          this.trackToInstrumentType[i] = this.instrumentTypes[idx];
          this.player.loader.startLoad(this.audioContext, info.url, info.variable);
        }

        this.scheduledNotes = [];
        let currentTimeOffset = 0;
        this.startsWithChorusFlag = startsWithChorus === true;

        let computedChorusStartTime: number | null = null;
        const verseSong = midiFile.parseSong(0, Infinity);

        if (this.hymnReference?.chorusMarker) {
          const mTrack = this.hymnReference.chorusMarker.track - 1;
          const targetNotes = verseSong.tracks[mTrack]?.notes || [];

          let sliceCount = 0;
          let lastStart = -1;
          for (const n of targetNotes) {
            if (lastStart === -1 || (n.when - lastStart) > this.GROUP_WINDOW_SEC) {
              lastStart = n.when;
              sliceCount++;
              if (sliceCount === this.hymnReference.chorusMarker.index) {
                computedChorusStartTime = n.when;
                break;
              }
            }
          }
        }

        this.chorusStartTime = computedChorusStartTime ?? this.hymnReference?.timestamps?.chorus ?? null;

        if (intro) {
          this.introPlaying = true;
          this.currentVerse = 0;
          const introSong = midiFile.parseSong(intro.start, intro.end);
          this.introDuration = introSong.duration;
          this.addNotesToSchedule(introSong.tracks, 0);

          if (this.scheduledNotes.length > 0 && !this.isGameMode) {
            let introEndTime = 0;
            for (const note of this.scheduledNotes) {
              const noteEndTime = note.when + note.duration;
              if (noteEndTime > introEndTime) {
                introEndTime = noteEndTime;
              }
            }
            this.introDuration = introEndTime;

            this.scheduledNotes.push({
              event: 'intro_ended',
              when: this.introDuration,
            });
          }
          currentTimeOffset = this.introDuration + (this.timeBetweenRepeats * 0.001);
        } else {
          this.introPlaying = false;
          this.currentVerse = 1;
          this.introDuration = 0;
          this.scheduledNotes.push({
            event: 'intro_ended',
            when: this.introDuration,
          });
        }

        let verseDuration = 0;
        verseSong.tracks.forEach((track: any) => {
          track.notes.forEach((note: any) => {
            const noteEndTime = note.when + note.duration;
            if (noteEndTime > verseDuration) {
              verseDuration = noteEndTime;
            }
          });
        });

        const verseDurationWithPause = verseDuration + (this.timeBetweenRepeats * 0.001);

        if(startsWithChorus && this.chorusStartTime !== null) {
          const chorusSong = midiFile.parseSong(this.chorusStartTime, Infinity);
          const chorusNotes = this.addNotesToSchedule(chorusSong.tracks, currentTimeOffset);

          let chorusSongDuration = 0;
          chorusSong.tracks.forEach((track: any) => {
            track.notes.forEach((note: any) => {
              const noteEndTime = note.when + note.duration;
              if (noteEndTime > chorusSongDuration) {
                chorusSongDuration = noteEndTime;
              }
            });
          });

          if (chorusNotes.length > 0) {
            this.scheduledNotes.push({
              event: 'chorus_start',
              when: chorusNotes[0].when
            });
          }

          if (chorusSongDuration > 0) {
            currentTimeOffset += chorusSongDuration + (this.timeBetweenRepeats * 0.001);
          }
        }

        for (let i = 0; i < this.totalRepeats; i++) {
          const verseNumber = i + 1;
          const notesForThisVerse = this.addNotesToSchedule(verseSong.tracks, currentTimeOffset);

          if (notesForThisVerse.length > 0) {
            this.scheduledNotes.push({
              event: `verse_start:${verseNumber}`,
              when: notesForThisVerse[0].when
            });
          }

          if (this.chorusStartTime) {
            this.scheduledNotes.push({
              event: 'chorus_start',
              when: this.chorusStartTime + currentTimeOffset
            });
          }

          currentTimeOffset += verseDurationWithPause;
        }

        this.scheduledNotes.sort((a, b) => {
          if (Math.abs(a.when - b.when) < 0.001) {
            if (a.event && !b.event) return -1;
            if (!a.event && b.event) return 1;
            return 0;
          }
          return a.when - b.when;
        });

        this.mainSongDuration = currentTimeOffset - this.introDuration;
        this.duration = currentTimeOffset - (this.timeBetweenRepeats > 0 ? (this.timeBetweenRepeats * 0.001) : 0);

        this.player.loader.waitLoad(() => {
          this.startPlay();
        });
      },
      error: (error) => console.error('Error loading MIDI file:', error),
    });
  }

  // Load main song and schedule a separate intro from a different MIDI buffer (e.g., /assets/intro/<ref>.mid)
  private loadAndBuildScheduleWithIntroBuffer(mainPath: string, introBuffer: ArrayBuffer, startsWithChorus: boolean = false) {
    this.http.get(mainPath, { responseType: 'arraybuffer' }).subscribe({
      next: (response) => {
        this.initAudioContext(false);
        this.player = new WebAudioFontPlayer();
        this.equalizer = this.player.createChannel(this.audioContext);
        this.reverberator = this.player.createReverberator(this.audioContext);

        this.masterGain = this.audioContext.createGain();
        this.masterGain.gain.value = 0.5; // -6 dB
        this.limiter = this.audioContext.createDynamicsCompressor();
        this.limiter.threshold.value = -6;
        this.limiter.knee.value = 4;
        this.limiter.ratio.value = 3;
        this.limiter.attack.value = 0.003;
        this.limiter.release.value = 0.25;

        this.input = this.equalizer.input;

        if (this.performanceModeEnabled) {
          this.equalizer.output.connect(this.limiter);
        } else {
          this.equalizer.output.connect(this.reverberator.input);
          this.reverberator.output.connect(this.limiter);
        }
        this.limiter.connect(this.masterGain);
        this.masterGain.connect(this.audioContext.destination);

        const midiFileMain = new MIDIFile(response);
        const songStructure = midiFileMain.parseSong();
        this.trackToInstrument = new Array(songStructure.tracks.length);

        const instrumentIds = this.loadInstrument(this.instrumentTypes);
        const numberOfInstruments = instrumentIds.length;
        for (let i = 0; i < songStructure.tracks.length; i++) {
          const idx = i % numberOfInstruments;
          const info = this.player.loader.instrumentInfo(instrumentIds[idx]);
          this.trackToInstrument[i] = info.variable;
          this.trackToInstrumentType[i] = this.instrumentTypes[idx];
          this.player.loader.startLoad(this.audioContext, info.url, info.variable);
        }

        this.scheduledNotes = [];
        let currentTimeOffset = 0;
        this.startsWithChorusFlag = startsWithChorus === true;

        let computedChorusStartTime: number | null = null;
        const verseSong = midiFileMain.parseSong(0, Infinity);

        if (this.hymnReference?.chorusMarker) {
          const mTrack = this.hymnReference.chorusMarker.track - 1;
          const targetNotes = verseSong.tracks[mTrack]?.notes || [];

          let sliceCount = 0;
          let lastStart = -1;
          for (const n of targetNotes) {
            if (lastStart === -1 || (n.when - lastStart) > this.GROUP_WINDOW_SEC) {
              lastStart = n.when;
              sliceCount++;
              if (sliceCount === this.hymnReference.chorusMarker.index) {
                computedChorusStartTime = n.when;
                break;
              }
            }
          }
        }

        this.chorusStartTime = computedChorusStartTime ?? this.hymnReference?.timestamps?.chorus ?? null;

        // Parse and schedule the separate intro MIDI
        try {
          const midiFileIntro = new MIDIFile(introBuffer);
          const introSong = midiFileIntro.parseSong(0, Infinity);
          this.introPlaying = true;
          this.currentVerse = 0;
          const notes = this.addNotesToScheduleRemapped(introSong.tracks, 0, songStructure.tracks.length);
          // Compute precise intro duration
          let introEndTime = 0;
          for (const note of notes) {
            const end = note.when + note.duration;
            if (end > introEndTime) introEndTime = end;
          }
          this.introDuration = introEndTime;
          this.scheduledNotes.push({ event: 'intro_ended', when: this.introDuration });
          currentTimeOffset = this.introDuration + (this.timeBetweenRepeats * 0.001);
        } catch (e) {
          console.warn('[MIDI] Failed to parse intro buffer, skipping separate intro and starting main song.', e);
          this.introPlaying = false;
          this.currentVerse = 1;
          this.introDuration = 0;
          this.scheduledNotes.push({ event: 'intro_ended', when: this.introDuration });
        }

        // Compute verse duration for main song
        let verseDuration = 0;
        verseSong.tracks.forEach((track: any) => {
          track.notes.forEach((note: any) => {
            const noteEndTime = note.when + note.duration;
            if (noteEndTime > verseDuration) {
              verseDuration = noteEndTime;
            }
          });
        });

        const verseDurationWithPause = verseDuration + (this.timeBetweenRepeats * 0.001);

        if (startsWithChorus && this.chorusStartTime !== null) {
          const chorusSong = midiFileMain.parseSong(this.chorusStartTime, Infinity);
          const chorusNotes = this.addNotesToSchedule(chorusSong.tracks, currentTimeOffset);

          let chorusSongDuration = 0;
          chorusSong.tracks.forEach((track: any) => {
            track.notes.forEach((note: any) => {
              const noteEndTime = note.when + note.duration;
              if (noteEndTime > chorusSongDuration) {
                chorusSongDuration = noteEndTime;
              }
            });
          });

          if (chorusNotes.length > 0) {
            this.scheduledNotes.push({ event: 'chorus_start', when: chorusNotes[0].when });
          }

          if (chorusSongDuration > 0) {
            currentTimeOffset += chorusSongDuration + (this.timeBetweenRepeats * 0.001);
          }
        }

        for (let i = 0; i < this.totalRepeats; i++) {
          const verseNumber = i + 1;
          const notesForThisVerse = this.addNotesToSchedule(verseSong.tracks, currentTimeOffset);

          if (notesForThisVerse.length > 0) {
            this.scheduledNotes.push({ event: `verse_start:${verseNumber}`, when: notesForThisVerse[0].when });
          }

          if (this.chorusStartTime) {
            this.scheduledNotes.push({ event: 'chorus_start', when: this.chorusStartTime + currentTimeOffset });
          }

          currentTimeOffset += verseDurationWithPause;
        }

        this.scheduledNotes.sort((a, b) => {
          if (Math.abs(a.when - b.when) < 0.001) {
            if (a.event && !b.event) return -1;
            if (!a.event && b.event) return 1;
            return 0;
          }
          return a.when - b.when;
        });

        this.mainSongDuration = currentTimeOffset - this.introDuration;
        this.duration = currentTimeOffset - (this.timeBetweenRepeats > 0 ? (this.timeBetweenRepeats * 0.001) : 0);

        this.player.loader.waitLoad(() => {
          this.startPlay();
        });
      },
      error: (error) => console.error('Error loading MAIN MIDI file:', error),
    });
  }

  private addNotesToSchedule(tracks: any[], offset: number): any[] {
    const addedNotes: any[] = [];
    tracks.forEach((track: any, trackIndex: number) => {
      track.notes.forEach((note: any) => {
        const newNote = {
          ...note,
          when: note.when + offset,
          trackIndex: trackIndex,
          volume: track.volume
        };
        this.scheduledNotes.push(newNote);
        addedNotes.push(newNote);
      });
    });
    return addedNotes;
  }

  // Variant for scheduling notes from a different file where the number of tracks may differ.
  // It remaps track indices to available instruments using modulo, to prevent out-of-bounds.
  private addNotesToScheduleRemapped(tracks: any[], offset: number, trackCapacity: number): any[] {
    const addedNotes: any[] = [];
    const safeCapacity = Math.max(1, trackCapacity);
    tracks.forEach((track: any, trackIndex: number) => {
      const mappedIndex = trackIndex % safeCapacity;
      track.notes.forEach((note: any) => {
        const newNote = {
          ...note,
          when: note.when + offset,
          trackIndex: mappedIndex,
          volume: track.volume
        };
        this.scheduledNotes.push(newNote);
        addedNotes.push(newNote);
      });
    });
    return addedNotes;
  }

  private startPlay() {
    this.zone.run(() => {
      this.progressSubject.next(0);
      this.eventSubject.next({ type: 'status', status: 'playing' });
    });
    this.startScheduling();
  }

  private startScheduling() {
    this.perTrackNoteIdx = new Array(this.trackToInstrument.length).fill(0);
    this.karaokeSlices = new Array(this.trackToInstrument.length).fill(null);
    this.nextNoteIndex = 0;
    this.songTimeOffset = 0;
    this.bufferedTime = 0;
    this.playing = true;

    this.audioTimeAtSpeedChange = this.audioContext.currentTime + 0.05;
    this.schedulerIntroPlaying = this.introPlaying;

    if (this.pendingStartAt) {
      const p = this.pendingStartAt;
      const tStart = this.computeTargetStartTime(p.section, p.verseNo, p.index, p.markerTrack);
      if (tStart != null) {
        this.songTimeOffset = tStart;
        this.bufferedTime = tStart;

        let i = 0;
        while (i < this.scheduledNotes.length && this.scheduledNotes[i].when < tStart) i++;
        this.nextNoteIndex = i;

        if (p.section === 'verse') {
          this.playingChorus = false;
          this.currentVerse = p.verseNo || 1;
          this.zone.run(() => this.eventSubject.next({ type: 'status', status: `verse_start:${this.currentVerse}` }));
        } else {
          this.playingChorus = true;
          this.zone.run(() => this.eventSubject.next({ type: 'status', status: 'chorus_start' }));
        }

        const track0 = Math.max(0, p.markerTrack - 1);
        this.perTrackNoteIdx[track0] = Math.max(0, p.index - 1);

        this.introPlaying = false;
        this.schedulerIntroPlaying = false;
      }
      this.pendingStartAt = undefined;
    }

    clearInterval(this.scheduler);
    clearInterval(this.progressInterval);

    this.schedulerTick();

    this.zone.runOutsideAngular(() => {
      this.scheduler = setInterval(() => this.schedulerTick(), 30);
      this.progressInterval = setInterval(() => this.updateProgress(), 1000);
    });
  }

  private schedulerTick() {
    if (!this.playing) {
      clearInterval(this.scheduler);
      return;
    }

    const baseLatency = (this.audioContext as any).baseLatency || 0.02;
    let lookaheadAudioTime = Math.min(1, Math.max(0.5, baseLatency * 20));
    if (this.performanceModeEnabled) {
      lookaheadAudioTime = Math.max(1.0, lookaheadAudioTime);
    }
    const lookaheadSongTime = lookaheadAudioTime * this.playbackSpeed;
    const currentSongTime = this.songTimeOffset + ((this.audioContext.currentTime - this.audioTimeAtSpeedChange) * this.playbackSpeed);
    const scheduleUntilSongTime = currentSongTime + lookaheadSongTime;

    if (this.bufferedTime < scheduleUntilSongTime) {
      this.sendNotes(this.bufferedTime, scheduleUntilSongTime);
      this.bufferedTime = scheduleUntilSongTime;
    }

    if (this.nextNoteIndex >= this.scheduledNotes.length) {
      clearInterval(this.scheduler);
      const remainingSongTime = this.duration - currentSongTime;
      const remainingAudioTime = remainingSongTime / this.playbackSpeed;
      const timeUntilEndMs = Math.max(0, remainingAudioTime * 1000);

      this.timeouts.push(setTimeout(() => {
        this.stop();
        this.eventSubject.next({ type: 'status', status: 'ended' });
      }, timeUntilEndMs));
    }
  }

  private sendNotes(startSongTime: number, endSongTime: number) {
    const hardwareLatency = (this.audioContext.outputLatency || this.audioContext.baseLatency || 0.05);

    while (this.nextNoteIndex < this.scheduledNotes.length && this.scheduledNotes[this.nextNoteIndex].when < endSongTime) {
      const note = this.scheduledNotes[this.nextNoteIndex];
      if (note.when >= startSongTime) {
        const songTimeFromMarker = note.when - this.songTimeOffset;
        const audioTimeFromMarker = songTimeFromMarker / this.playbackSpeed;
        const when = this.audioTimeAtSpeedChange + audioTimeFromMarker;

        if (note.event) {
          if (note.event === 'intro_ended') {
            this.schedulerIntroPlaying = false;
          } else if (note.event === 'chorus_start' || (typeof note.event === 'string' && note.event.startsWith('verse_start:'))) {
            this.perTrackNoteIdx = new Array(this.trackToInstrument.length).fill(0);
            this.karaokeSlices = new Array(this.trackToInstrument.length).fill(null);
          }

          const msUntilEvent = Math.max(0, ((when - this.audioContext.currentTime + hardwareLatency) * 1000) - 10);
          this.timeouts.push(setTimeout(() => {
            this.handleNoteEventUI(note.event);
          }, msUntilEvent));
        }

        if (note.pitch !== undefined && note.duration !== undefined) {
          const duration = note.duration / this.playbackSpeed;

          if (!this.schedulerIntroPlaying) {
            const t = note.trackIndex;
            if (this.perTrackNoteIdx[t] === undefined) {
              this.perTrackNoteIdx[t] = 0;
            }

            if (!this.karaokeSlices[t]) {
              const idx = ++this.perTrackNoteIdx[t];
              const slice = { start: when, end: when + duration, idx } as any;

              const msUntilOn = Math.max(0, (slice.start - this.audioContext.currentTime + hardwareLatency) * 1000);
              slice.onTimeout = setTimeout(() => {
                this.zone.run(() => {
                  this.eventSubject.next({ type: 'note', track: t + 1, index: slice.idx, state: 'on' });
                });
              }, msUntilOn);
              this.timeouts.push(slice.onTimeout);

              const msUntilOff = Math.max(0, (slice.end - this.audioContext.currentTime + hardwareLatency) * 1000);
              slice.offTimeout = setTimeout(() => {
                this.zone.run(() => {
                  this.eventSubject.next({ type: 'note', track: t + 1, index: slice.idx, state: 'off' });
                });
              }, msUntilOff);
              this.timeouts.push(slice.offTimeout);

              this.karaokeSlices[t] = slice;
            } else {
              const slice = this.karaokeSlices[t]!;

              if (when <= slice.start + this.GROUP_WINDOW_SEC) {
                const newEnd = Math.max(slice.end, when + duration);
                if (newEnd !== slice.end) {
                  slice.end = newEnd;
                  if (slice.offTimeout) clearTimeout(slice.offTimeout);

                  const msUntilOff = Math.max(0, (slice.end - this.audioContext.currentTime + hardwareLatency) * 1000);
                  slice.offTimeout = setTimeout(() => {
                    this.zone.run(() => {
                      this.eventSubject.next({ type: 'note', track: t + 1, index: slice.idx, state: 'off' });
                    });
                  }, msUntilOff);
                  this.timeouts.push(slice.offTimeout);
                }
              } else {
                if (slice.end > when) {
                  slice.end = when;
                  if (slice.offTimeout) clearTimeout(slice.offTimeout);

                  const msUntilOff = Math.max(0, (slice.end - this.audioContext.currentTime + hardwareLatency) * 1000);
                  slice.offTimeout = setTimeout(() => {
                    this.zone.run(() => {
                      this.eventSubject.next({ type: 'note', track: t + 1, index: slice.idx, state: 'off' });
                    });
                  }, msUntilOff);
                  this.timeouts.push(slice.offTimeout);
                }

                const idx = ++this.perTrackNoteIdx[t];
                const newSlice = { start: when, end: when + duration, idx } as any;

                const msUntilOn = Math.max(0, (newSlice.start - this.audioContext.currentTime + hardwareLatency) * 1000);
                newSlice.onTimeout = setTimeout(() => {
                  this.zone.run(() => {
                    this.eventSubject.next({ type: 'note', track: t + 1, index: newSlice.idx, state: 'on' });
                  });
                }, msUntilOn);
                this.timeouts.push(newSlice.onTimeout);

                const msUntilOff = Math.max(0, (newSlice.end - this.audioContext.currentTime + hardwareLatency) * 1000);
                newSlice.offTimeout = setTimeout(() => {
                  this.zone.run(() => {
                    this.eventSubject.next({ type: 'note', track: t + 1, index: newSlice.idx, state: 'off' });
                  });
                }, msUntilOff);
                this.timeouts.push(newSlice.offTimeout);

                this.karaokeSlices[t] = newSlice;
              }
            }
          }

          const instrument = this.trackToInstrument[note.trackIndex];
          const instrumentType = this.trackToInstrumentType[note.trackIndex];
          let volume = note.volume / 7;

          if (this.instrumentTypes && this.instrumentTypes.length > 1 && instrumentType === this.instrumentTypes[1]) {
            volume = volume * this.ACCOMP_VOLUME_MULTIPLIER;
          }

          const transposedPitch = Math.max(0, Math.min(127, (note.pitch ?? 0) + this.transposeSemitones));

          if (instrumentType === 'voice') {
            const targetGain = this.audioContext.createGain();
            targetGain.gain.setValueAtTime(0.00001, when);

            const attack = Math.min(this.VOICE_ATTACK_SEC, Math.max(0, duration * 0.25));
            const release = Math.min(this.VOICE_RELEASE_SEC, Math.max(0, duration * 0.5));
            const sustainStart = when + attack;
            const releaseStart = Math.max(when + attack, when + duration - release);

            targetGain.connect(this.input);
            targetGain.gain.linearRampToValueAtTime(volume, sustainStart);
            targetGain.gain.setValueAtTime(volume, releaseStart);
            targetGain.gain.linearRampToValueAtTime(0.00001, when + duration);

            this.player.queueWaveTable(this.audioContext, targetGain, (window as any)[instrument], when, transposedPitch, duration, 1.0, note.slides);
          } else {
            this.player.queueWaveTable(this.audioContext, this.input, (window as any)[instrument], when, transposedPitch, duration, volume, note.slides);
          }
        }
      }
      this.nextNoteIndex++;
    }
  }

  private handleNoteEventUI(event: string) {
    this.zone.run(() => {
      if (event.startsWith('verse_start:')) {
        this.playingChorus = false;
        this.currentVerse = parseInt(event.split(':')[1], 10);
        this.eventSubject.next({ type: 'status', status: `verse_changed:${this.currentVerse}` });
      } else if (event === 'chorus_start') {
        this.playingChorus = true;
        this.eventSubject.next({ type: 'status', status: 'chorus_start' });
      } else if (event === 'intro_ended') {
        this.introPlaying = false;
        this.mainSongAudioTimeStart = this.audioContext.currentTime;
        this.eventSubject.next({ type: 'status', status: 'intro_ended' });
      }
    });
  }

  private updateProgress() {
    if (!this.playing || !this.audioContext) return;

    const currentSongTime = this.songTimeOffset + ((this.audioContext.currentTime - this.audioTimeAtSpeedChange) * this.playbackSpeed);

    let progress = 0;
    if (this.introPlaying) {
      if (this.introDuration > 0) {
        progress = Math.min(100, (currentSongTime / this.introDuration) * 100);
      }
    } else {
      const elapsedMainSongTime = currentSongTime - this.introDuration;
      if (this.mainSongDuration > 0) {
        progress = Math.min(100, (elapsedMainSongTime / this.mainSongDuration) * 100);
      }
    }

    this.zone.run(() => this.progressSubject.next(progress));
  }

  private instrumentMap = {
    "piano": 0, "organ": 1, "violin": 2, "harp": 3, "flute": 4,
    "guitar": 5, "ensemble": 6, "electric_piano": 7, "synth": 8, "voice": 9, "music_box": 10,
  };

  private loadInstrument(instrumentTypes: string[]) {
    const resultInstrumentIds = [];
    for (let i = 0; i < instrumentTypes.length; i++) {
      let instrumentType = instrumentTypes[i];
      resultInstrumentIds.push(this.instrumentMap[instrumentType as keyof typeof this.instrumentMap] || this.instrumentMap["piano"]);
    }
    return resultInstrumentIds;
  }

  replaceRandomInstruments(instrumentTypes: string[]): string[] {
    const result = [];
    const enabled = this.settingsService.getEnabledInstruments?.() ?? Object.keys(this.instrumentMap);
    const pool = (enabled && enabled.length > 0) ? enabled.filter(k => this.instrumentMap[k as keyof typeof this.instrumentMap] !== undefined) : (Object.keys(this.instrumentMap));
    for (let i = 0; i < instrumentTypes.length; i++) {
      if (instrumentTypes[i] !== "random") {
        result.push(instrumentTypes[i]);
      } else {
        const instrumentType = pool[Math.floor(Math.random() * pool.length)] as keyof typeof this.instrumentMap;
        result.push(instrumentType);
      }
    }
    return result;
  }

  private async getSongDuration(path: string): Promise<number> {
    return new Promise((resolve, reject) => {
      this.http.get(path, { responseType: 'arraybuffer' }).subscribe({
        next: (response) => {
          const midiFile = new MIDIFile(response);
          const song = midiFile.parseSong(0, Infinity);

          let trueDuration = 0;
          song.tracks.forEach((track: any) => {
            track.notes.forEach((note: any) => {
              const noteEndTime = note.when + note.duration;
              if (noteEndTime > trueDuration) {
                trueDuration = noteEndTime;
              }
            });
          });

          resolve(trueDuration);
        }, error: (error) => {
          console.error('Error loading MIDI file:', error);
          reject(error);
        }
      });
    });
  }

  private resetSchedulers() {
    if (this.timeouts) {
      this.timeouts.forEach((timeout: any) => clearTimeout(timeout));
      this.timeouts = [];
    }
    if (this.scheduler) {
      clearInterval(this.scheduler);
      this.scheduler = null;
    }
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }
    this.bufferedTime = 0;
    this.appleSilentBackgroundSound.pause();
  }

  releaseAudioContextIfNotUsedSoon() {
    if (this.audioContextTimeout) {
      clearTimeout(this.audioContextTimeout);
    }
    this.audioContextTimeout = setTimeout(() => {
      if (!this.playing && this.audioContext) {
        console.debug("Closing audio context after 10 seconds of inactivity");
        this.audioContext.suspend();
        this.audioContext.close();
        this.audioContext = null;
      }
    }, 10000);
  }

  initAudioContext(releaseAfterTimeout = true) {
    try {
      this.startAppleBackgroundSilence();
    } catch (error) {
      console.warn("Failed to start Apple background silence audio:", error);
    }

    if (this.audioContextTimeout) {
      clearTimeout(this.audioContextTimeout);
    }
    if (!this.audioContext || this.audioContext.state === 'closed') {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && (navigator as any).maxTouchPoints > 1);
      const latencyHint = isIOS ? 'interactive' : 'playback';
      const AudioContextFunc = (window as any).AudioContext || (window as any).webkitAudioContext;
      this.audioContext = new AudioContextFunc({
        latencyHint: latencyHint
      });

      this.performanceModeEnabled = this.detectPerformanceMode((this.audioContext as any).baseLatency);

      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' && this.audioContext?.state === 'suspended') {
          this.audioContext.resume();
        }
      });

      if (releaseAfterTimeout) {
        this.releaseAudioContextIfNotUsedSoon();
      }
    }
  }

  private detectPerformanceMode(baseLatency?: number): boolean {
    try {
      const hc = (navigator as any).hardwareConcurrency || 2;
      const dm = (navigator as any).deviceMemory || 2;

      const isLowCore = hc <= 4;
      const isLowMem = dm && dm <= 3;
      const highLatency = typeof baseLatency === 'number' && baseLatency > 0.05;

      return isLowCore || isLowMem || highLatency;
    } catch (e) {
      return false;
    }
  }

  handleControlsEvent(action: any) {
    const message = action.message;
    switch (message) {
      case 'music-controls-next':
        if (this.nextButtonAction) this.nextButtonAction();
        break;
      case 'music-controls-previous':
        if (this.audioContext && this.audioContext.currentTime > 3) {
          this.restart();
        } else {
          if (this.previousButtonAction) this.previousButtonAction();
        }
        break;
      case 'music-controls-pause':
        this.stop();
        break;
      case 'music-controls-play':
        this.restart();
        break;
      case 'music-controls-destroy':
        this.stop();
        break;
      case 'music-controls-toggle-play-pause':
        if (this.playing) this.stop();
        else this.restart();
        break;
      case 'music-controls-headset-unplugged':
        this.stop();
        break;
    }
  }

  setNextButtonAction(action: () => void) { this.nextButtonAction = action; }
  setPreviousButtonAction(action: () => void) { this.previousButtonAction = action; }
  clearNextButtonAction() { this.nextButtonAction = null; }
  clearPreviousButtonAction() { this.previousButtonAction = null; }

  public setPlaybackSpeed(speed: number) {
    if (!this.playing || !this.audioContext) {
      this.playbackSpeed = speed;
      return;
    }

    const now = this.audioContext.currentTime;
    const elapsedAudioTime = now - this.audioTimeAtSpeedChange;

    this.songTimeOffset += elapsedAudioTime * this.playbackSpeed;

    this.audioTimeAtSpeedChange = now;
    this.playbackSpeed = speed;
  }

  private speedStep = 0.1;

  increasePlaybackSpeed() {
    if (this.playbackSpeed < 2.0) this.setPlaybackSpeed(this.playbackSpeed + this.speedStep);
  }

  decreasePlaybackSpeed() {
    if (this.playbackSpeed > 0.5) this.setPlaybackSpeed(this.playbackSpeed - this.speedStep);
  }

  startSpeedChangeInterval(increaseSpeed: boolean = true): void {
    this.stopSpeedChangeInterval();
    this.speedChangeInterval = setInterval(() => {
      if (increaseSpeed) this.increasePlaybackSpeed();
      else this.decreasePlaybackSpeed();
    }, 400);
  }

  stopSpeedChangeInterval(): void {
    if (this.speedChangeInterval) {
      clearInterval(this.speedChangeInterval);
      this.speedChangeInterval = null;
    }
  }

  private readonly MIN_TRANSPOSE = -12;
  private readonly MAX_TRANSPOSE = 12;

  public getTranspose(): number {
    return this.transposeSemitones;
  }

  public async setTranspose(semitones: number) {
    const clamped = Math.max(this.MIN_TRANSPOSE, Math.min(this.MAX_TRANSPOSE, semitones));
    this.transposeSemitones = clamped;
    console.info('[Audio] Transpose set to', clamped, 'semitones');
    await this.settingsService.setPlaybackTranspose(clamped);
  }

  public async incrementTranspose(step: number) {
    await this.setTranspose(this.transposeSemitones + step);
  }

  public async resetTranspose() {
    await this.setTranspose(0);
  }
}
